/**
 * Once the tab knows it runs a stale build, the NEXT in-app navigation to a
 * different page is done as a full page load of the destination URL instead of
 * a router transition (Q-506 card 1573, Q-494 a). Nothing is shown.
 *
 * - Never on a timer or tab return: only a router navigation triggers it.
 * - Only pathname changes that push or pop: a filter change in the same page,
 *   or a replace (redirect), is not a page the person asked for.
 * - Skipped while any dialog is open (`openDialogCount`, the same signal
 *   q506-1546 uses for "an unsaved edit is open"); the navigation then goes
 *   through in-app and the reload waits for the next one with nothing open.
 *   A page-level editor with a pending save is protected by its own
 *   `beforeunload` prompt (useEditorView), which a full load triggers.
 * - At most once per deployed build per tab, and not at all when session
 *   storage is unavailable (see `canReloadForBuild`).
 *
 * Needs a data router (`useBlocker`).
 */
import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { canReloadForBuild, markReloadedForBuild } from '@/api/deployedVersion';
import { openDialogCount } from '@/components/dialogs/StyledDialog/dialogStack';
import { useDeployedVersionCheck } from './useDeployedVersionCheck';

export function useDeployedBuildReload(): void {
  const staleHash = useDeployedVersionCheck();
  const staleHashRef = useRef<string | null>(null);
  staleHashRef.current = staleHash;

  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    const hash = staleHashRef.current;
    if (!hash) return false;
    if (historyAction === 'REPLACE') return false;
    if (currentLocation.pathname === nextLocation.pathname) return false;
    if (openDialogCount() > 0) return false;
    return canReloadForBuild(hash);
  });

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const hash = staleHashRef.current;
    if (hash && markReloadedForBuild(hash)) {
      const { pathname, search, hash: fragment } = blocker.location;
      window.location.assign(`${pathname}${search}${fragment}`);
    } else {
      blocker.proceed();
    }
  }, [blocker]);
}
